/**
 * Pure validation helpers for the checkout form, extracted from CheckoutPage so
 * section completion can be derived from data (not from which button was tapped)
 * and tested in isolation. The predicates mirror the presence checks the
 * Save & Continue handlers perform; `collectFieldErrors` mirrors the format
 * checks run just before an order is submitted.
 */

export interface MemberInfoFields {
  fullName: string;
  streetAddress: string;
  apt?: string;
  city: string;
  state: string;
  zipCode: string;
  phoneNumber: string;
  emailAddress: string;
}

export interface ShippingInfoFields {
  shippingSame: boolean;
  shippingMethod?: string;
  fullName: string;
  streetAddress: string;
  apt?: string;
  city: string;
  state: string;
  zipCode: string;
}

export interface BillingInfoFields {
  cardNumber: string;
  expirationDate: string;
  cvv: string;
  nameOnCard: string;
  billingSame?: boolean;
}

export type ProtectionChoice = 'yes' | 'no' | null;

export type CheckoutSectionKey = 'memberInfo' | 'protectDevice' | 'shippingInfo' | 'billingInfo';

export const CHECKOUT_SECTION_ORDER: CheckoutSectionKey[] = [
  'memberInfo',
  'protectDevice',
  'shippingInfo',
  'billingInfo',
];

export function isMemberInfoComplete(info: MemberInfoFields): boolean {
  return !!(
    info.fullName &&
    info.streetAddress &&
    info.city &&
    info.state &&
    info.zipCode &&
    info.phoneNumber &&
    info.emailAddress
  );
}

/** A protection choice counts once the shopper picked yes/no, or a plan is already in the cart. */
export function isProtectionChosen(choice: ProtectionChoice, protectionPlanInCart: boolean): boolean {
  return protectionPlanInCart || choice !== null;
}

export function isShippingComplete(info: ShippingInfoFields): boolean {
  if (info.shippingSame) return true;
  return !!(info.fullName && info.streetAddress && info.city && info.state && info.zipCode);
}

export function isBillingComplete(info: BillingInfoFields, options: { cvvOptional: boolean }): boolean {
  return !!(
    info.cardNumber &&
    info.expirationDate &&
    info.nameOnCard &&
    (options.cvvOptional || info.cvv)
  );
}

export interface SectionCompletion {
  memberInfo: boolean;
  protectDevice: boolean;
  shippingInfo: boolean;
  billingInfo: boolean;
}

export function deriveSectionCompletion(input: {
  memberInfo: MemberInfoFields;
  protectionPlan: ProtectionChoice;
  protectionPlanInCart: boolean;
  shippingInfo: ShippingInfoFields;
  billingInfo: BillingInfoFields;
  cvvOptional: boolean;
}): SectionCompletion {
  return {
    memberInfo: isMemberInfoComplete(input.memberInfo),
    protectDevice: isProtectionChosen(input.protectionPlan, input.protectionPlanInCart),
    shippingInfo: isShippingComplete(input.shippingInfo),
    billingInfo: isBillingComplete(input.billingInfo, { cvvOptional: input.cvvOptional }),
  };
}

/** First section (in display order) that is not complete, or 'review' when all are. */
export function firstIncompleteSection(completion: SectionCompletion): CheckoutSectionKey | 'review' {
  for (const key of CHECKOUT_SECTION_ORDER) {
    if (!completion[key]) return key;
  }
  return 'review';
}

export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const PHONE_REGEX = /^[\d\s\-()]+$/;
/** MM/YY or MM/YYYY; browser autofill often supplies a 4-digit year. */
export const CARD_EXPIRY_REGEX = /^\d{2}\/(\d{2}|\d{4})$/;
export const CVV_REGEX = /^\d{3,4}$/;

/**
 * One actionable checkout problem: a message for the inline error list and a
 * focus target that matches a control's `name` attribute or a `data-testid`.
 */
export interface CheckoutIssue {
  message: string;
  focus: string;
}

/**
 * Field-level issues for the member, protection, shipping and billing sections,
 * in focus order. Cart-level checks (empty cart, missing pricing) live with the
 * caller. `collectFieldErrors` is the message-only view of the same list, minus
 * the protection choice (kept for callers that already check that separately).
 */
export function listCheckoutIssues(input: {
  memberInfo: MemberInfoFields;
  shippingInfo: ShippingInfoFields;
  billingInfo: BillingInfoFields;
  cvvOptional: boolean;
  protectionPlan?: ProtectionChoice;
  protectionPlanInCart?: boolean;
}): CheckoutIssue[] {
  const {
    memberInfo,
    shippingInfo,
    billingInfo,
    cvvOptional,
    protectionPlan = null,
    protectionPlanInCart = false,
  } = input;
  const issues: CheckoutIssue[] = [];

  if (!memberInfo.fullName?.trim()) issues.push({ message: 'Full name is required', focus: 'memberInfo.fullName' });
  if (!memberInfo.streetAddress?.trim()) issues.push({ message: 'Street address is required', focus: 'memberInfo.streetAddress' });
  if (!memberInfo.city?.trim()) issues.push({ message: 'City is required', focus: 'memberInfo.city' });
  if (!memberInfo.state?.trim()) issues.push({ message: 'State is required', focus: 'memberInfo.state' });
  if (!memberInfo.zipCode?.trim()) issues.push({ message: 'Zip code is required', focus: 'memberInfo.zipCode' });
  if (!memberInfo.phoneNumber?.trim()) issues.push({ message: 'Phone number is required', focus: 'memberInfo.phoneNumber' });
  if (!memberInfo.emailAddress?.trim()) issues.push({ message: 'Email address is required', focus: 'memberInfo.emailAddress' });

  if (memberInfo.emailAddress && !EMAIL_REGEX.test(memberInfo.emailAddress)) {
    issues.push({ message: 'Invalid email address format', focus: 'memberInfo.emailAddress' });
  }

  if (memberInfo.phoneNumber && !PHONE_REGEX.test(memberInfo.phoneNumber.replace(/\s/g, ''))) {
    issues.push({ message: 'Invalid phone number format', focus: 'memberInfo.phoneNumber' });
  }

  if (!isProtectionChosen(protectionPlan, protectionPlanInCart)) {
    issues.push({ message: 'Please choose whether you want device protection', focus: 'protection-options' });
  }

  if (!shippingInfo.shippingSame) {
    if (!shippingInfo.fullName?.trim()) issues.push({ message: 'Shipping name is required', focus: 'shippingInfo.fullName' });
    if (!shippingInfo.streetAddress?.trim()) issues.push({ message: 'Shipping address is required', focus: 'shippingInfo.streetAddress' });
    if (!shippingInfo.city?.trim()) issues.push({ message: 'Shipping city is required', focus: 'shippingInfo.city' });
    if (!shippingInfo.state?.trim()) issues.push({ message: 'Shipping state is required', focus: 'shippingInfo.state' });
    if (!shippingInfo.zipCode?.trim()) issues.push({ message: 'Shipping zip code is required', focus: 'shippingInfo.zipCode' });
  }

  // Visual form order: Name on Card, then Number / MM/YY / CVC.
  if (!billingInfo.nameOnCard?.trim()) issues.push({ message: 'Name on card is required', focus: 'billingInfo.nameOnCard' });
  if (!billingInfo.cardNumber?.trim()) issues.push({ message: 'Card number is required', focus: 'billingInfo.cardNumber' });
  if (!billingInfo.expirationDate?.trim()) issues.push({ message: 'Expiration date is required', focus: 'billingInfo.expirationDate' });
  if (!cvvOptional && !billingInfo.cvv?.trim()) issues.push({ message: 'CVV is required', focus: 'billingInfo.cvv' });

  const cardNumberClean = billingInfo.cardNumber?.replace(/\s/g, '') || '';
  if (cardNumberClean && (cardNumberClean.length < 13 || cardNumberClean.length > 19)) {
    issues.push({ message: 'Card number must be 13-19 digits', focus: 'billingInfo.cardNumber' });
  }

  if (billingInfo.expirationDate && !CARD_EXPIRY_REGEX.test(billingInfo.expirationDate)) {
    issues.push({ message: 'Expiration date must be in MM/YY or MM/YYYY format', focus: 'billingInfo.expirationDate' });
  }

  if (billingInfo.cvv && !CVV_REGEX.test(billingInfo.cvv)) {
    issues.push({ message: 'CVV must be 3-4 digits', focus: 'billingInfo.cvv' });
  }

  return issues;
}

/** Message-only field errors (protection choice excluded; callers check that separately). */
export function collectFieldErrors(input: {
  memberInfo: MemberInfoFields;
  shippingInfo: ShippingInfoFields;
  billingInfo: BillingInfoFields;
  cvvOptional: boolean;
}): string[] {
  return listCheckoutIssues({
    ...input,
    // Treat protection as already chosen so this list stays field-only.
    protectionPlan: 'no',
    protectionPlanInCart: false,
  }).map(issue => issue.message);
}

/**
 * Cart/pricing guards shared by desktop `validateCheckoutData` and the mobile
 * Place Order path. Keeps submission from proceeding when a product is in the
 * cart but catalog pricing never resolved.
 */
export function collectCartLevelErrors(input: {
  cartItems: Array<{ type?: string }>;
  pricingData: unknown;
}): string[] {
  const errors: string[] = [];
  if (!input.cartItems.length) {
    errors.push('Cart is empty');
  }
  const hasProduct = input.cartItems.some(item => item.type === 'product');
  if (hasProduct && !input.pricingData) {
    errors.push('Pricing data is missing for product in cart');
  }
  return errors;
}
